import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BadgeCheck, Camera, Check, ChevronDown, ClipboardCheck, FileSearch, FileText,
  KeyRound, Megaphone, MessagesSquare, Palette, Phone, Sparkles, TrendingUp, X,
} from "lucide-react";
import { LINKS, trackEvent } from "../../data";
import { CATALOG, SERVICE_IMAGES, SERVICE_ORDER } from "./catalog";

const EASE = [0.22, 1, 0.36, 1];

const ICONS = {
  Megaphone, FileSearch, FileText, MessagesSquare, TrendingUp,
  KeyRound, ClipboardCheck, Sparkles, Camera, Palette,
};

const waLink = (msg) => `${LINKS.whatsapp}?text=${encodeURIComponent(msg)}`;

function PriceBlock({ service, c }) {
  if (service.quote) {
    return <p className="font-display text-xl font-semibold italic text-med">{c.quote}</p>;
  }
  if (service.offers) {
    return (
      <div className="space-y-2">
        {service.offers.map((o) => (
          <div key={o.name} className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-xs font-extrabold uppercase tracking-wider text-navy">{o.name}:</span>
            {o.launch ? (
              <>
                <span className="text-sm font-semibold text-mute line-through">{o.price}</span>
                <span className="font-display text-xl font-semibold text-navy">{o.launch}</span>
              </>
            ) : (
              <span className="font-display text-xl font-semibold text-navy">{o.price}</span>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-sm font-semibold text-mute line-through">
        {c.listino} {service.price}
      </span>
      <span className="font-display text-2xl font-semibold text-navy">{service.launch}</span>
      <span className="rounded-full bg-sky/15 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-navy">
        {c.lancio}
      </span>
    </div>
  );
}

function ServiceCard({ service, c, open, onToggle, onRequest }) {
  const Icon = ICONS[service.icon] || Sparkles;
  const panelId = `service-panel-${service.id}`;
  const image = SERVICE_IMAGES[service.id];

  return (
    <div
      data-testid={`service-card-${service.id}`}
      className="flex flex-col overflow-hidden rounded-[1.75rem] bg-white ring-1 ring-navy/10 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(15,43,58,0.3)]"
    >
      {image && (
        <div className="relative h-44 overflow-hidden">
          <img
            src={image}
            alt={service.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
            data-testid={`service-image-${service.id}`}
          />
          <span
            className={`absolute right-3 top-3 rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] ring-1 backdrop-blur-md ${
              service.remote ? "bg-white/85 text-med ring-sky/40" : "bg-white/85 text-navy ring-navy/20"
            }`}
          >
            {service.remote ? c.remoteBadge : c.onsiteBadge}
          </span>
        </div>
      )}
      <div className="flex flex-1 flex-col p-7">
        <div className="flex items-start justify-between gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-navy text-sky">
            <Icon className="h-5 w-5" />
          </span>
          {!image && (
            <span
              className={`rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] ring-1 ${
                service.remote ? "bg-sky/10 text-med ring-sky/30" : "bg-sand text-navy ring-navy/15"
              }`}
            >
              {service.remote ? c.remoteBadge : c.onsiteBadge}
            </span>
          )}
        </div>

        <h3 className="mt-5 font-display text-xl font-semibold leading-snug text-navy">{service.title}</h3>
        <p className="mt-2 text-sm italic leading-relaxed text-mute">“{service.tagline}”</p>

        <div className="mt-4">
          <PriceBlock service={service} c={c} />
        </div>

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            type="button"
            data-testid={`service-expand-${service.id}`}
            aria-expanded={open}
            aria-controls={panelId}
            onClick={onToggle}
            className="inline-flex items-center gap-2 rounded-full bg-navy/5 px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider text-navy ring-1 ring-navy/10 transition-colors hover:bg-navy hover:text-white"
          >
            {open ? c.close : c.discover}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <button
            type="button"
            data-testid={`service-request-${service.id}`}
            onClick={() => onRequest(service.title)}
            className="inline-flex items-center gap-2 rounded-full bg-sky px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider text-navy transition-colors hover:bg-navy hover:text-white"
          >
            {c.request}
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              id={panelId}
              data-testid={panelId}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-6 space-y-5 border-t border-navy/10 pt-6">
                <p className="text-sm leading-relaxed text-ink/80">{service.description}</p>

                {service.offers ? (
                  service.offers.map((o) => (
                    <div key={o.name} className="rounded-2xl bg-warm p-5 ring-1 ring-navy/10">
                      <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{o.name}</p>
                      <p className="mt-1.5 text-sm font-bold text-med">
                        {o.launch ? (
                          <>
                            <span className="mr-2 font-semibold text-mute line-through">{o.price}</span>
                            {o.launch}
                          </>
                        ) : (
                          o.price
                        )}
                      </p>
                      {o.launchNote && <p className="mt-1 text-xs font-semibold text-mute">{o.launchNote}</p>}
                      <ul className="mt-3 space-y-1.5">
                        {o.included.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-ink/80">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-med" strokeWidth={3} />
                            {item}
                          </li>
                        ))}
                        {o.excluded.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-mute">
                            <X className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                ) : (
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{c.includedLabel}</p>
                    <ul className="mt-2.5 space-y-1.5">
                      {service.included.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-ink/80">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-med" strokeWidth={3} />
                          {item}
                        </li>
                      ))}
                    </ul>
                    {service.excluded && service.excluded.length > 0 && (
                      <>
                        <p className="mt-4 text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{c.excludedLabel}</p>
                        <ul className="mt-2.5 space-y-1.5">
                          {service.excluded.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-xs leading-relaxed text-mute">
                              <X className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={3} />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}

                {service.timing && (
                  <p className="text-xs leading-relaxed text-ink/70">
                    <span className="font-extrabold uppercase tracking-wider text-navy">{c.timingLabel}: </span>
                    {service.timing}
                  </p>
                )}
                {service.conditions && (
                  <p className="text-xs leading-relaxed text-ink/70">
                    <span className="font-extrabold uppercase tracking-wider text-navy">{c.conditionsLabel}: </span>
                    {service.conditions}
                  </p>
                )}

                <div className="flex flex-wrap gap-2.5 pt-1">
                  <a
                    data-testid={`service-whatsapp-${service.id}`}
                    href={waLink(c.waMessage(service.title))}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("whatsapp_click_owner", { service: service.title, source: "servizi" })}
                    className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-white transition-colors hover:bg-med"
                  >
                    {c.whatsapp}
                  </a>
                  <a
                    data-testid={`service-phone-${service.id}`}
                    href={LINKS.phone}
                    onClick={() => trackEvent("phone_click", { service: service.title, source: "servizi" })}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-xs font-extrabold uppercase tracking-wider text-navy ring-1 ring-navy/20 transition-colors hover:bg-sand"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {c.call}
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export function ServicesCatalog({ lang, onRequest, openServiceId }) {
  const c = CATALOG[lang];
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (openServiceId) setOpenId(openServiceId);
  }, [openServiceId]);

  const sorted = [...c.services].sort(
    (a, b) => SERVICE_ORDER.indexOf(a.id) - SERVICE_ORDER.indexOf(b.id)
  );

  const toggle = (service) => {
    const opening = openId !== service.id;
    setOpenId(opening ? service.id : null);
    if (opening) trackEvent("service_card_open", { service: service.title, source: "servizi" });
  };

  return (
    <section id="catalogo-servizi" data-testid="services-catalog" className="bg-sand py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <p className="kicker text-navy">
            <span className="h-px w-10 bg-sky" />
            {c.kicker}
          </p>
          <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-navy sm:text-5xl">
            {c.title}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-mute sm:text-lg">{c.sub}</p>
          <div data-testid="launch-offer-note" className="mt-6 inline-flex items-start gap-3 rounded-2xl bg-white px-5 py-4 ring-1 ring-sky/30">
            <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-med" />
            <p className="text-xs font-bold leading-relaxed text-navy">
              {c.launchNote}
              <span className="mt-0.5 block font-semibold text-mute">{c.launchSub}</span>
            </p>
          </div>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {sorted.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              c={c}
              open={openId === service.id}
              onToggle={() => toggle(service)}
              onRequest={onRequest}
            />
          ))}
        </div>

        <div className="mt-14 rounded-[2rem] bg-navy px-8 py-10 text-center text-white sm:px-12" data-testid="catalog-call-cta">
          <p className="font-display text-2xl font-semibold sm:text-3xl">{c.callCta}</p>
          <p className="mt-3 text-sm text-white/65">{c.callCtaSub}</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <a
              data-testid="catalog-whatsapp-cta"
              href={waLink(c.waMessage(lang === "en" ? "your services" : "i vostri servizi"))}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click_owner", { source: "servizi_catalogo" })}
              className="btn-primary"
            >
              {c.whatsapp}
            </a>
            <a
              data-testid="catalog-phone-cta"
              href={LINKS.phone}
              onClick={() => trackEvent("phone_click", { source: "servizi_catalogo" })}
              className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold text-white ring-1 ring-white/40 transition-colors hover:bg-white/10"
            >
              <Phone className="h-4 w-4" />
              {c.call}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
